import { useTranslation } from 'react-i18next';

import { DataPanelAutoWrapInlineItem, DataPanelItem, DataPanelRow, DebugView } from '@/ui';
import type { PanelItemColor } from '@/ui';
import { internetConnectionLabel } from '@/signalr';
import type { Caller, InternetConnectivity, InternetConnectionType } from '@/signalr';

type DeviceInfo = NonNullable<Caller['deviceInfo']>;

export type DeviceStatusRowProps = {
  deviceInfo: DeviceInfo | null;
  internetConnectionType: InternetConnectionType | null;
};

const internetConnectivityLabelKeys = {
  Online: 'Incident_InfoPanel_InternetConnectivity_Online',
  Offline: 'Incident_InfoPanel_InternetConnectivity_Offline',
} as const satisfies Record<Exclude<InternetConnectivity, 'Unknown'>, string>;

function batteryColor(level: number | null): PanelItemColor {
  if (level == null) {
    return 'none';
  }

  return level > 50 ? 'excellent' : level > 20 ? 'warning' : 'critical';
}

function internetConnectivityColor(internetConnectivity: InternetConnectivity): PanelItemColor {
  switch (internetConnectivity) {
    case 'Online':
      return 'excellent';
    case 'Offline':
      return 'critical';
    case 'Unknown':
      return 'none';
  }
}

function connectionColor(connectionType: InternetConnectionType): PanelItemColor {
  switch (connectionType) {
    case 'NoConnection':
    case 'Cellular2G':
      return 'critical';
    case 'Cellular3G':
      return 'warning';
    case 'Cellular4G':
    case 'Cellular5G':
    case 'WiFi':
      return 'excellent';
    case 'Unknown':
      return 'none';
  }
}

function combinedConnectionColor(
  internetConnectivity: InternetConnectivity,
  connectionType: InternetConnectionType | null,
): PanelItemColor {
  const colors = [
    internetConnectivityColor(internetConnectivity),
    connectionType == null ? 'none' : connectionColor(connectionType),
  ];

  if (colors.includes('critical')) {
    return 'critical';
  }

  if (colors.includes('warning')) {
    return 'warning';
  }

  return colors.includes('excellent') ? 'excellent' : 'none';
}

export function DeviceStatusRow({ deviceInfo, internetConnectionType }: DeviceStatusRowProps) {
  const { t, i18n } = useTranslation();

  if (deviceInfo === null) {
    return <DataPanelAutoWrapInlineItem title={t('Incident_InfoPanel_Device')} showEmptyPlaceholder />;
  }

  const battery =
    deviceInfo.batteryLevel == null
      ? undefined
      : `${new Intl.NumberFormat(i18n.language || 'en', { maximumFractionDigits: 0 }).format(deviceInfo.batteryLevel)} %`;

  const internetConnectivity =
    deviceInfo.internetConnectivity === 'Unknown'
      ? undefined
      : t(internetConnectivityLabelKeys[deviceInfo.internetConnectivity]);

  const knownConnectionType =
    internetConnectionType === null || internetConnectionType === 'Unknown' ? null : internetConnectionType;

  const connection = [
    internetConnectivity,
    knownConnectionType == null ? undefined : internetConnectionLabel(knownConnectionType),
  ]
    .filter((value): value is string => value != null)
    .join(', ');

  return (
    <>
      <DataPanelRow>
        <DataPanelItem
          title={t('Incident_InfoPanel_System')}
          value={deviceInfo.system === 'Unknown' ? undefined : deviceInfo.system}
          showEmptyPlaceholder
        />

        <DataPanelItem
          title={t('Incident_InfoPanel_InternetConnectivity')}
          value={connection || undefined}
          color={combinedConnectionColor(deviceInfo.internetConnectivity, knownConnectionType)}
          contentCssClass="text-center"
          showEmptyPlaceholder
        />

        <DataPanelItem
          title={t('Incident_InfoPanel_BatteryLevel')}
          value={battery}
          color={batteryColor(deviceInfo.batteryLevel)}
          contentCssClass="text-end"
          showEmptyPlaceholder
        />
      </DataPanelRow>

      <DebugView>
        <DataPanelAutoWrapInlineItem
          title={t('Incident_InfoPanel_SystemVersion')}
          value={deviceInfo.systemVersion ?? undefined}
          showEmptyPlaceholder
        />

        <DataPanelAutoWrapInlineItem
          title={t('Incident_InfoPanel_LiveVideoSupport')}
          value={t(`Incident_InfoPanel_LiveVideoSupport_${deviceInfo.liveVideoSupport}`)}
        />
      </DebugView>
    </>
  );
}
